<script setup lang="ts">
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { api } from '@/api';
import { routenames } from '@/router/routenames';
import { JobConfigurationDto, JobConfigurationDtoTypeEnum } from '@/api/generator';
import { onMounted } from 'vue';
import { useLoading } from '@/utils/useLoading';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import { useRouter } from 'vue-router';
import { toCreateOrUpdateJobConfigurationDto } from './mapping';
import { useToken } from '@/utils/tokens/tokens';
import JobConfigurationForm from './components/JobConfigurationForm.vue';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { entityNotFoundErrorHandler } from '../../errorHandling/errorHandler';
import { backendValidator } from '@/validation/validators/backendValidator';

interface JobEditViewProps {
  id: string;
}

const props = defineProps<JobEditViewProps>();
const router = useRouter();

const { action: loadEquipment, loading } = useLoading(async () => {
  const { result } = await api.jobConfigurationsApi.getJobConfiguration(
    {
      id: props.id,
    },
    [entityNotFoundErrorHandler(router)],
  );

  if (result) {
    resetForm({ values: result });
  }
}, true);

const { form } = useVeeForm<JobConfigurationDto>({
  options: {
    keepValuesOnUnmount: true, // keep values when switching between tabs
  },
  schemaName: 'JobConfigurationDto',
  mode: 'edit',
  customValidateFunctions: [
    backendValidator((jobConfigurationDto, errorHandlers) =>
      api.jobConfigurationsApi.validateJobConfiguration(
        {
          createOrUpdateJobConfigurationDto:
            toCreateOrUpdateJobConfigurationDto(jobConfigurationDto),
        },
        errorHandlers,
      ),
    ),
  ],
});

const { handleSubmit, resetForm, values } = form;

const token = useToken({
  id: props.id,
  $type:
    JobConfigurationDtoTypeEnum.PtwPtw100AppZeroApplicationSharedJobConfigurationComponentDtoJobConfigurationDtoPtwPtw100AppZeroApplicationShared,
});

onMounted(token.request);

token.on.initialized(loadEquipment);

token.on.inUse(() => {
  router.push({
    name: routenames.jobConfigurations.overview,
  });
});

const submit = handleSubmit(
  async (jobConfigurationDto) => {
    const { success } = await api.jobConfigurationsApi.updateJobConfiguration(
      {
        id: props.id,
        createOrUpdateJobConfigurationDto: toCreateOrUpdateJobConfigurationDto(jobConfigurationDto),
      },
      [formBackendErrorHandler(form)],
    );

    if (success) {
      await router.push({
        name: routenames.jobConfigurations.overview,
      });
    }
  },
  {
    withReferenceCheck: true,
  },
);
</script>

<template>
  <form @submit="submit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle :title="values.name" :titleId="values.iuid" />
      </template>

      <template #status>
        <BasePageStatus :entity="values" />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.jobConfigurations.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>

      <JobConfigurationForm />
    </BasePage>
  </form>
</template>
